/**
 * Onboarding Config Service (UA-5.2)
 *
 * CRUD operations for the `onboarding_step_config` table, which stores
 * per-invite-type step requirement overrides (required / optional / hidden).
 *
 * Follows the service pattern from `src/services/campaigns.ts`.
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "./logger";
import type { InviteType } from "@/types/InviteType";
import type {
  OnboardingStepConfig,
  OnboardingStepRequirement,
  UpsertStepConfigInput,
} from "@/types/OnboardingConfig";

// ---------------------------------------------------------------------------
// Row mapper — snake_case DB row → typed interface
// ---------------------------------------------------------------------------

function mapRow(row: Record<string, unknown>): OnboardingStepConfig {
  return {
    id: row.id as string,
    invite_type: row.invite_type as InviteType,
    step_id: row.step_id as string,
    requirement: row.requirement as OnboardingStepRequirement,
    display_order: (row.display_order as number) ?? 0,
    updated_by: (row.updated_by as string) ?? null,
    updated_at: row.updated_at as string,
  };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const onboardingConfigService = {
  /** Fetch all step configurations across every invite type. */
  async getStepConfigs(): Promise<OnboardingStepConfig[]> {
    try {
      logger.info("Fetching all onboarding step configs");
      const { data, error } = await supabase
        .from("onboarding_step_config")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) {
        logger.error("Error fetching onboarding step configs", error);
        throw error;
      }
      return (data || []).map(mapRow);
    } catch (error: unknown) {
      logger.error("Error in onboardingConfigService.getStepConfigs", error);
      throw error;
    }
  },

  /** Fetch step configurations for a specific invite type. */
  async getStepConfigsByInviteType(
    inviteType: InviteType,
  ): Promise<OnboardingStepConfig[]> {
    try {
      logger.info("Fetching onboarding step configs by invite type", {
        inviteType,
      });
      const { data, error } = await supabase
        .from("onboarding_step_config")
        .select("*")
        .eq("invite_type", inviteType)
        .order("display_order", { ascending: true });

      if (error) {
        logger.error(
          "Error fetching onboarding step configs by invite type",
          error,
          { inviteType },
        );
        throw error;
      }
      return (data || []).map(mapRow);
    } catch (error: unknown) {
      logger.error(
        "Error in onboardingConfigService.getStepConfigsByInviteType",
        error,
        { inviteType },
      );
      throw error;
    }
  },

  /** Create or update a single step configuration row. */
  async upsertStepConfig(
    inviteType: InviteType,
    stepId: string,
    requirement: OnboardingStepRequirement,
  ): Promise<OnboardingStepConfig> {
    try {
      logger.info("Upserting onboarding step config", {
        inviteType,
        stepId,
        requirement,
      });
      const { data, error } = await supabase
        .from("onboarding_step_config")
        .upsert(
          {
            invite_type: inviteType,
            step_id: stepId,
            requirement,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "invite_type,step_id" },
        )
        .select("*")
        .single();

      if (error) {
        logger.error("Error upserting onboarding step config", error, {
          inviteType,
          stepId,
        });
        throw error;
      }
      return mapRow(data);
    } catch (error: unknown) {
      logger.error(
        "Error in onboardingConfigService.upsertStepConfig",
        error,
        { inviteType, stepId },
      );
      throw error;
    }
  },

  /** Batch upsert multiple step configurations in a single call. */
  async bulkUpsertStepConfigs(
    configs: UpsertStepConfigInput[],
  ): Promise<OnboardingStepConfig[]> {
    try {
      logger.info("Bulk upserting onboarding step configs", {
        count: String(configs.length),
      });

      const rows = configs.map((c) => ({
        invite_type: c.invite_type,
        step_id: c.step_id,
        requirement: c.requirement,
        updated_at: new Date().toISOString(),
      }));

      const { data, error } = await supabase
        .from("onboarding_step_config")
        .upsert(rows, { onConflict: "invite_type,step_id" })
        .select("*");

      if (error) {
        logger.error("Error bulk upserting onboarding step configs", error);
        throw error;
      }
      return (data || []).map(mapRow);
    } catch (error: unknown) {
      logger.error(
        "Error in onboardingConfigService.bulkUpsertStepConfigs",
        error,
      );
      throw error;
    }
  },
};
